import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchSubscriptions,
  fetchSubscriptionsAll,
  createSubscription,
  updateSubscription,
  deleteSubscription,
  fetchSubscriptionSummary,
  fetchSubscriptionUsageReport,
  fetchSubscriptionDetails,
  createManualPayment,
} from "./thunks";
import { toast } from "react-toastify";

interface Subscription {
  id: string | number;
  client_id: string | number;
  subscription_id: string | number;
  // Adicione mais campos conforme necessário
}

interface PaginatedSubscriptions {
  data: Subscription[];
  total?: number;
  currentPage?: number;
  perPage?: number;
}

interface SubscriptionsState {
  subscriptions: PaginatedSubscriptions;
  subscriptionsAll: Subscription[]; // Lista de todas as assinaturas sem paginação
  usageReport: any; // Relatório de uso de assinatura
  subscriptionDetails: any; // Detalhes da assinatura
  loading: boolean;
  loadingAll: boolean; // Loading específico para subscriptionsAll
  loadingUsageReport: boolean; // Loading específico para relatório de uso
  loadingSubscriptionDetails: boolean; // Loading específico para detalhes da assinatura
  error: string | null;
  subscriptionSummary?: any;
}

const initialState: SubscriptionsState = {
  subscriptions: { data: [] },
  subscriptionsAll: [], // Lista de todas as assinaturas sem paginação
  usageReport: null, // Relatório de uso de assinatura
  subscriptionDetails: null, // Detalhes da assinatura
  loading: false,
  loadingAll: false, // Loading específico para subscriptionsAll
  loadingUsageReport: false, // Loading específico para relatório de uso
  loadingSubscriptionDetails: false, // Loading específico para detalhes da assinatura
  error: null,
  subscriptionSummary: null,
};

const subscriptionsSlice = createSlice({
  name: "subscriptions",
  initialState,
  reducers: {
    clearSubscriptions: (state) => {
      state.subscriptions = { data: [] };
      state.loading = false;
      state.error = null;
    },
    clearSubscriptionsAll: (state) => {
      state.subscriptionsAll = [];
      state.loadingAll = false;
    },
    clearUsageReport: (state) => {
      state.usageReport = null;
      state.loadingUsageReport = false;
    },
    clearSubscriptionDetails: (state) => {
      state.subscriptionDetails = null;
      state.loadingSubscriptionDetails = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Manual Payment
      .addCase(createManualPayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createManualPayment.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          toast.success("Pagamento manual criado com sucesso!");
        },
      )
      .addCase(createManualPayment.rejected, (state, action) => {
        console.log("error payload", action.payload);
        state.loading = false;
        // The thunk now forwards the API response body when available.
        // Try to read a user-friendly message from action.payload.message or action.payload.error
        const payload = action.payload as any;
        const message =
          payload?.message ||
          payload?.error ||
          "Erro ao criar pagamento manual!";
        state.error = message;
        toast.error(message);
      })
      // Fetch
      .addCase(fetchSubscriptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchSubscriptions.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const payload = action.payload;

          if (Array.isArray(payload.data)) {
            state.subscriptions = { data: payload.data };
          } else {
            state.subscriptions = {
              data: payload.data.data,
              total: payload.total,
              currentPage: payload.current_page,
              perPage: payload.per_page,
            };
          }
        },
      )
      .addCase(fetchSubscriptions.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
      })
      // Fetch Subscriptions All
      .addCase(fetchSubscriptionsAll.pending, (state) => {
        state.loadingAll = true;
        state.error = null;
      })
      .addCase(
        fetchSubscriptionsAll.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loadingAll = false;
          const payload = action.payload;
          // Como é without_pagination, sempre será um array direto
          state.subscriptionsAll = payload.data || [];
        },
      )
      .addCase(fetchSubscriptionsAll.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loadingAll = false;
      })

      // Create
      .addCase(createSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createSubscription.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.subscriptions.data.push(action.payload.data);
          // Também adicionar ao subscriptionsAll se existir
          state.subscriptionsAll.push(action.payload.data);
          toast.success("Assinatura criada com sucesso!");
        },
      )
      .addCase(createSubscription.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        toast.error("Erro ao criar assinatura. Tente novamente!");
      })

      // Update
      .addCase(updateSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateSubscription.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const index = state.subscriptions.data.findIndex(
            (s) => s.id == action.payload.data.id,
          );
          if (index !== -1) {
            state.subscriptions.data[index] = action.payload.data;
          }
          // Também atualizar no subscriptionsAll se existir
          const indexAll = state.subscriptionsAll.findIndex(
            (s) => s.id == action.payload.data.id,
          );
          if (indexAll !== -1) {
            state.subscriptionsAll[indexAll] = action.payload.data;
          }
          toast.success("Assinatura atualizada com sucesso!");
        },
      )
      .addCase(updateSubscription.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        toast.error("Erro ao atualizar assinatura!");
      })

      // Delete
      .addCase(deleteSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deleteSubscription.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.subscriptions.data = state.subscriptions.data.filter(
            (s) => String(s.id) !== action.payload.data.id,
          );
          // Também remover do subscriptionsAll
          state.subscriptionsAll = state.subscriptionsAll.filter(
            (s) => String(s.id) !== action.payload.data.id,
          );
          toast.success("Assinatura excluída com sucesso!");
        },
      )
      .addCase(deleteSubscription.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        toast.error("Erro ao excluir assinatura!");
      })

      .addCase(fetchSubscriptionSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchSubscriptionSummary.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.subscriptionSummary = action.payload?.data;
        },
      )
      .addCase(fetchSubscriptionSummary.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
      })

      // Fetch Subscription Usage Report
      .addCase(fetchSubscriptionUsageReport.pending, (state) => {
        state.loadingUsageReport = true;
        state.error = null;
      })
      .addCase(
        fetchSubscriptionUsageReport.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loadingUsageReport = false;
          state.usageReport = action.payload;
        },
      )
      .addCase(fetchSubscriptionUsageReport.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loadingUsageReport = false;
        toast.error("Erro ao carregar relatório de uso de assinatura!");
      })

      // Fetch Subscription Details
      .addCase(fetchSubscriptionDetails.pending, (state) => {
        state.loadingSubscriptionDetails = true;
        state.error = null;
      })
      .addCase(
        fetchSubscriptionDetails.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loadingSubscriptionDetails = false;
          state.subscriptionDetails = action.payload;
        },
      )
      .addCase(fetchSubscriptionDetails.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loadingSubscriptionDetails = false;
        toast.error("Erro ao carregar detalhes da assinatura!");
      });
  },
});

export const {
  clearSubscriptions,
  clearSubscriptionsAll,
  clearUsageReport,
  clearSubscriptionDetails,
} = subscriptionsSlice.actions;
export default subscriptionsSlice.reducer;
